import React, { useState, useRef, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tv,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  RefreshCw,
  Plus,
  Minus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// Base URL for IPTV-org M3U playlists
const IPTV_BASE = "https://iptv-org.github.io/iptv/countries";

// Curated Israeli channels with known working stream URLs
const ISRAEL_CHANNELS: Channel[] = [
  {
    name: "כאן 11 | Kan 11",
    url: "https://kan11w.media.kan.org.il/hls/live/2105694/2105694/master.m3u8",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Kan11Logo.svg/220px-Kan11Logo.svg.png",
    group: "שידור ציבורי"
  },
  {
    name: "כאן חינוכית | Kan Chinuchit",
    url: "https://kan23.media.kan.org.il/hls/live/2024691/2024691/master.m3u8",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/KanHinuchit.svg/220px-KanHinuchit.svg.png",
    group: "שידור ציבורי"
  },
  {
    name: "מכאן 33 | Makan 33",
    url: "https://kancdn.medonecdn.net/livehls/oil/kancdn-live/live/makan/live.livx/playlist.m3u8",
    logo: "https://i.imgur.com/RVtQTed.png",
    group: "שידור ציבורי"
  },
  {
    name: "ערוץ 13 | Reshet 13",
    url: "https://d2xg1g9o5vns8m.cloudfront.net/out/v1/0855d703f7d5436fae6a9c7ce8ca5075/index.m3u8",
    logo: "https://upload.wikimedia.org/wikipedia/he/thumb/5/5a/Ch13logo.svg/150px-Ch13logo.svg.png",
    group: "ערוצים מסחריים"
  },
  {
    name: "ערוץ הכנסת | Knesset Channel",
    url: "https://contact.gostreaming.tv/Knesset/myStream/playlist.m3u8",
    logo: "https://i.imgur.com/QXB1jv8.png",
    group: "ערוצים ציבוריים"
  },
  {
    name: "i24NEWS עברית",
    url: "https://bcovlive-a.akamaihd.net/d89ede8094c741b7924120b27764153c/eu-central-1/5377161796001/profile_0/chunklist.m3u8",
    logo: "https://www.i24news.tv/images/favicon.png",
    group: "חדשות"
  },
  {
    name: "i24NEWS ערבית",
    url: "https://bcovlive-a.akamaihd.net/95116e8d79524d87bf3ac20ba04241e3/eu-central-1/5377161796001/playlist.m3u8",
    logo: "https://www.i24news.tv/images/favicon.png",
    group: "חדשות"
  },
  {
    name: "i24NEWS צרפתית",
    url: "https://bcovlive-a.akamaihd.net/41814196d97e433fb401c5e632d985e9/eu-central-1/5377161796001/playlist.m3u8",
    logo: "https://www.i24news.tv/images/favicon.png",
    group: "חדשות"
  },
  {
    name: "קשת 12 | N12",
    url: "",
    logo: "https://upload.wikimedia.org/wikipedia/he/thumb/8/8a/Keshet_12_Logo.svg/200px-Keshet_12_Logo.svg.png",
    group: "ערוצים מסחריים",
    dynamicSource: "mako-stream"
  },
  {
    name: "ערוץ 9 | Channel 9",
    url: "http://50.7.231.221:8081/185/index.m3u8?wmsAuthSign=okad",
    logo: "https://i.imgur.com/pttM3KQ.png",
    group: "חדשות"
  },
  {
    name: "הידברות | Hidabroot",
    url: "https://cdn.cybercdn.live/HidabrootIL/Live97/playlist.m3u8",
    logo: "https://i.imgur.com/CTH4Hu2.png",
    group: "כללי"
  },
  {
    name: "הלא TV | Hala TV",
    url: "https://stream.panet.com/edge/halaTV/playlist.m3u8",
    logo: "https://i.imgur.com/028M4Ew.png",
    group: "כללי"
  },
  {
    name: "Relevant TV",
    url: "https://6180c994cb835402.mediapackage.eu-west-1.amazonaws.com/out/v1/f1339272dd24416ca60b00e69075d783/index.m3u8",
    logo: "https://i.imgur.com/jCZ5gFO.png",
    group: "חדשות"
  },
  {
    name: "קבלה לעם | Kabbalah",
    url: "https://edge3.uk.kab.tv/live/tv66-heb-high/playlist.m3u8",
    logo: "https://i.imgur.com/VxAHOgr.png",
    group: "כללי"
  },
  {
    name: "Angel TV עברית",
    url: "https://janya-digimix.akamaized.net/vglive-sk-150533/hebrew/ngrp:angelhebrew_all/playlist.m3u8",
    logo: "https://i.imgur.com/qKLEGU7.png",
    group: "כללי"
  },
];

// Blocked content categories (case-insensitive)
const BLOCKED_CATEGORIES = [
  'adult', 'xxx', '18+', 'porn', 'erotic', 'sex', 'mature',
  'violence', 'gore', 'horror', 'gambling', 'casino'
];

// Blocked keywords in channel names (case-insensitive)
const BLOCKED_KEYWORDS = [
  'adult', 'xxx', 'porn', 'erotic', 'playboy', 'penthouse', 
  'hustler', '18+', 'sex', 'nude', 'naked', 'casino', 'bet365'
];

// Check if a channel should be blocked
const isBlockedChannel = (channel: { name: string; group?: string }): boolean => {
  const nameLower = channel.name.toLowerCase();
  const groupLower = (channel.group || '').toLowerCase();
  
  // Check blocked categories
  for (const blocked of BLOCKED_CATEGORIES) {
    if (groupLower.includes(blocked.toLowerCase())) {
      return true;
    }
  }
  
  // Check blocked keywords in name
  for (const keyword of BLOCKED_KEYWORDS) {
    if (nameLower.includes(keyword.toLowerCase())) {
      return true;
    }
  }
  
  return false;
};

// Country data with ISO codes for IPTV-org
const getCountries = (t: (key: string) => string) => [
  { id: "il", name: t("israel"), flag: "🇮🇱", code: "il" },
  { id: "us", name: t("usa"), flag: "🇺🇸", code: "us" },
  { id: "ru", name: t("russiaCountry"), flag: "🇷🇺", code: "ru" },
  { id: "sa", name: t("saudiArabia"), flag: "🇸🇦", code: "sa" },
  { id: "world", name: t("restOfWorld"), flag: "🌍", code: "int" },
];

interface Channel {
  name: string;
  url: string;
  logo?: string;
  group?: string;
  /** If set, the URL is fetched dynamically via this edge function */
  dynamicSource?: string;
}

export default function TVPage() {
  const { t, dir } = useTranslation();
  const countries = getCountries(t);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentChannel, setCurrentChannel] = useState<Channel | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Parse M3U content to extract channels
  const parseM3U = (content: string): Channel[] => {
    const lines = content.split('\n');
    const channelList: Channel[] = [];
    let currentChannel: Partial<Channel> = {};

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('#EXTINF:')) {
        // Extract channel info from #EXTINF line
        const logoMatch = line.match(/tvg-logo="([^"]+)"/);
        const groupMatch = line.match(/group-title="([^"]+)"/);
        const nameMatch = line.match(/,(.+)$/);
        
        currentChannel = {
          logo: logoMatch?.[1],
          group: groupMatch?.[1],
          name: nameMatch?.[1]?.trim() || ''
        };
      } else if (line && !line.startsWith('#') && (line.startsWith('http') || line.startsWith('https'))) {
        // This is a URL
        if (currentChannel.name) {
          channelList.push({
            name: currentChannel.name,
            url: line,
            logo: currentChannel.logo,
            group: currentChannel.group
          });
        }
        currentChannel = {};
      }
    }

    return channelList;
  };

  // Load channels for selected country
  const loadChannels = async (countryCode: string) => {
    setLoading(true);
    setChannels([]);
    setError(null);

    // Use curated list for Israel
    if (countryCode === 'il') {
      setChannels(ISRAEL_CHANNELS);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${IPTV_BASE}/${countryCode}.m3u`);
      if (response.ok) {
        const content = await response.text();
        const parsedChannels = parseM3U(content);
        const safeChannels = parsedChannels.filter(ch => !isBlockedChannel(ch));
        setChannels(safeChannels);
        if (safeChannels.length === 0) {
          setError(t("noChannelsFound"));
        }
      } else {
        setError(t("channelLoadError"));
      }
    } catch {
      setError(t("channelLoadError"));
    }
    
    setLoading(false);
  };

  // Handle country selection
  const handleCountrySelect = (countryId: string) => {
    const country = countries.find(c => c.id === countryId);
    if (country) {
      setSelectedCountry(countryId);
      loadChannels(country.code);
    }
  };

  // Handle channel play with HLS support
  // Store HLS instance for cleanup
  const hlsRef = useRef<any>(null);

  const playChannel = async (channel: Channel) => {
    setCurrentChannel(channel);
    setError(null);
    setIsPlaying(false);

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }

    if (!videoRef.current) return;

    // Resolve dynamic URL if needed
    let streamUrl = channel.url;
    if (channel.dynamicSource) {
      try {
        const { data, error: fnError } = await supabase.functions.invoke(channel.dynamicSource);
        if (fnError || !data?.url) {
          setError(t("channelPlayError"));
          return;
        }
        streamUrl = data.url;
      } catch {
        setError(t("channelPlayError"));
        return;
      }
    }

    if (!streamUrl) {
      setError(t("channelPlayError"));
      return;
    }

    const video = videoRef.current;

    // Check if it's an HLS stream
    if (streamUrl.includes('.m3u8')) {
      // Check for native HLS support (Safari)
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = streamUrl;
        try {
          await video.play();
          setIsPlaying(true);
        } catch {
          setError(t("channelPlayError"));
        }
      } else {
        // For other browsers, use hls.js
        try {
          const Hls = (await import('hls.js')).default;
          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: true,
            });
            hlsRef.current = hls;
            hls.loadSource(streamUrl);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              video.play().then(() => {
                setIsPlaying(true);
              }).catch(() => {
                setError(t("channelPlayError"));
              });
            });
            hls.on(Hls.Events.ERROR, (_event: string, data: any) => {
              if (data.fatal) {
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                  hls.startLoad(); // try to recover
                } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                  hls.recoverMediaError(); // try to recover
                } else {
                  setError(t("channelError"));
                }
              }
            });
          } else {
            setError(t("formatNotSupported"));
          }
        } catch {
          // hls.js failed to load, try direct
          video.src = streamUrl;
          try {
            await video.play();
            setIsPlaying(true);
          } catch {
            setError(t("channelPlayError"));
          }
        }
      }
    } else {
      // Direct stream URL
      video.src = streamUrl;
      try {
        await video.play();
        setIsPlaying(true);
      } catch {
        setError(t("channelPlayError"));
      }
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Resume failed silently
      });
    }
  };

  // Handle volume change
  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (videoRef.current) {
      videoRef.current.volume = newVolume / 100;
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  // Go back to country selection
  const goBack = () => {
    setSelectedCountry(null);
    setChannels([]);
    setCurrentChannel(null);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
    setIsPlaying(false);
  };

  const selectedCountryData = countries.find(c => c.id === selectedCountry);

  // Group channels by category
  const groupedChannels = channels.reduce((acc, channel) => {
    const group = channel.group || t("generalCategory");
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(channel);
    return acc;
  }, {} as Record<string, Channel[]>);

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background" dir={dir} ref={containerRef}>
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Tv className="w-8 h-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">{t("tv")}</h1>
            </div>
            
            {/* Back is now handled by sidebar */}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {!selectedCountry ? (
            // Country Selection
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-3xl">
                {countries.map((country) => (
                    <Button
                      key={country.id}
                      variant="outline"
                      data-scan-item
                      onClick={() => handleCountrySelect(country.id)}
                      className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-primary/10 hover:border-primary transition-all"
                    >
                      <span className="text-5xl">{country.flag}</span>
                      <span className="text-base font-medium">{country.name}</span>
                    </Button>
                  ))}
              </div>
            </div>
          ) : (
            // Channel List and Video Player
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Video Player */}
              <div className="flex-1 flex flex-col bg-black">
                <div className="relative flex-1 flex items-center justify-center">
                  <video
                    ref={videoRef}
                    className={cn("w-full h-full object-contain", !currentChannel && "hidden")}
                    playsInline
                  />
                  {currentChannel && error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                      <div className="text-center">
                        <p className="text-white mb-4">{error}</p>
                        <Button data-scan-item onClick={() => playChannel(currentChannel)} variant="outline" className="gap-2">
                          <RefreshCw className="w-4 h-4" />
                          {t("tryAgain")}
                        </Button>
                      </div>
                    </div>
                  )}
                  {!currentChannel && (
                    <div className="text-center text-white/60">
                      <Tv className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>{t("selectChannel")}</p>
                    </div>
                  )}
                </div>

                {/* Video Controls */}
                {currentChannel && (
                  <div className="p-4 bg-black/90 flex items-center gap-4 flex-wrap">
                    <Button
                      variant="ghost"
                      data-scan-item
                      onClick={togglePlayPause}
                      className="text-white hover:bg-white/20 h-14 w-14"
                    >
                      {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                    </Button>

                    <div className="flex-1 text-white text-lg font-medium min-w-0">
                      <span className="block whitespace-normal break-words">
                        {currentChannel.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Volume Down */}
                      <Button
                        variant="ghost"
                        data-scan-item
                        onClick={() => handleVolumeChange([Math.max(0, volume - 10)])}
                        className="text-white hover:bg-white/20 h-12 w-12"
                      >
                        <Minus className="w-6 h-6" />
                      </Button>
                      
                      {/* Volume Display */}
                      <span className="text-white text-lg font-bold min-w-[3ch] text-center">
                        {isMuted ? 0 : volume}
                      </span>
                      
                      {/* Volume Up */}
                      <Button
                        variant="ghost"
                        data-scan-item
                        onClick={() => handleVolumeChange([Math.min(100, volume + 10)])}
                        className="text-white hover:bg-white/20 h-12 w-12"
                      >
                        <Plus className="w-6 h-6" />
                      </Button>

                      {/* Mute Button */}
                      <Button
                        variant="ghost"
                        data-scan-item
                        onClick={toggleMute}
                        className="text-white hover:bg-white/20 h-14 w-14"
                      >
                        {isMuted ? <VolumeX className="w-8 h-8" /> : <Volume2 className="w-8 h-8" />}
                      </Button>
                      
                      {/* Fullscreen Button */}
                      <Button
                        variant="ghost"
                        data-scan-item
                        onClick={toggleFullscreen}
                        className="text-white hover:bg-white/20 h-14 w-14"
                      >
                        {isFullscreen ? <Minimize className="w-8 h-8" /> : <Maximize className="w-8 h-8" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Channel List Sidebar */}
              <div className="group/sidebar w-full md:w-20 hover:md:w-96 border-t md:border-t-0 md:border-r border-border bg-card transition-all duration-300 ease-in-out">
                <div className="p-3 border-b border-border">
                  <div className="flex items-center gap-2 justify-center md:group-hover/sidebar:justify-start">
                    <span className="text-2xl">{selectedCountryData?.flag}</span>
                    <h2 className="font-bold text-foreground hidden md:group-hover/sidebar:block">{selectedCountryData?.name}</h2>
                    <span className="text-sm text-muted-foreground hidden md:group-hover/sidebar:inline">({channels.length} ערוצים)</span>
                  </div>
                </div>

                <ScrollArea className="h-[300px] md:h-[calc(100vh-250px)]">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
                    </div>
                  ) : Object.keys(groupedChannels).length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      {error || "לא נמצאו ערוצים"}
                    </div>
                  ) : (
                    <div className="p-2 space-y-4">
                      {Object.entries(groupedChannels).map(([group, groupChannels]) => (
                        <div key={group}>
                          <h3 className="text-sm font-semibold text-muted-foreground px-2 mb-2 hidden md:group-hover/sidebar:block">
                            {group}
                          </h3>
                          <div className="space-y-1">
                            {groupChannels.map((channel, index) => (
                              <button
                                key={`${channel.name}-${index}`}
                                data-scan-item
                                onClick={() => playChannel(channel)}
                                className={cn(
                                  "w-full p-2 rounded-lg flex items-center gap-2 transition-all justify-center md:group-hover/sidebar:justify-start",
                                  "hover:bg-primary/10",
                                  currentChannel?.url === channel.url
                                    ? "bg-primary/20 border border-primary"
                                    : "bg-background"
                                )}
                              >
                                {channel.logo ? (
                                  <img 
                                    src={channel.logo} 
                                    alt={channel.name}
                                    className="w-10 h-10 object-contain rounded flex-shrink-0"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-muted rounded flex items-center justify-center flex-shrink-0">
                                    <Tv className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                )}
                                <span className="flex-1 text-sm font-medium text-foreground whitespace-normal break-words text-right hidden md:group-hover/sidebar:block">
                                  {channel.name}
                                </span>
                                {currentChannel?.url === channel.url && isPlaying && (
                                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse hidden md:group-hover/sidebar:block" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
